import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', planType: 'free' });
  const [createUserResult, setCreateUserResult] = useState(null);
  const [emailCheckStatus, setEmailCheckStatus] = useState(null); // null, 'checking', 'available', 'taken'
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [autoApprove, setAutoApprove] = useState(null); // null while loading
  const [autoApproveSaving, setAutoApproveSaving] = useState(false);

  // Load users
  const loadUsers = async (page = 1, search = searchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });
      if (search) {
        params.append('search', search);
      }

      const response = await axios.get(`/admin/users?${params.toString()}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load users:', error);
      alert('Failed to load users: ' + error.message);
    }
    setLoading(false);
  };

  // Grant role
  const grantRole = async (userId, role) => {
    try {
      await axios.post(`/admin/users/${userId}/roles`, {
        role,
        notes: `Granted via admin panel by ${user.name}`
      });
      alert(`${role} role granted successfully!`);
      loadUsers(pagination.page);
      setShowRoleModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Failed to grant role:', error);
      alert('Failed to grant role: ' + (error.response?.data?.error || error.message));
    }
  };

  // Revoke role
  const revokeRole = async (userId, roleId) => {
    if (!window.confirm('Are you sure you want to revoke this role?')) return;
    
    try {
      await axios.delete(`/admin/users/${userId}/roles/${roleId}`);
      alert('Role revoked successfully!');
      loadUsers(pagination.page);
    } catch (error) {
      console.error('Failed to revoke role:', error);
      // Check for specific error messages from backend
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      alert('Failed to revoke role: ' + errorMessage);
    }
  };

  // Update subscription
  const updateSubscription = async (userId, planType) => {
    if (!window.confirm(`Change subscription to ${planType.toUpperCase()}?`)) return;

    try {
      await axios.put(`/subscriptions/admin/${userId}`, { planType });
      alert('Subscription updated successfully!');
      loadUsers(pagination.page);
      setShowSubscriptionModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Failed to update subscription:', error);
      alert('Failed to update subscription: ' + error.message);
    }
  };

  // Delete user
  const deleteUser = async (userId, userName) => {
    const confirmMessage = `Are you sure you want to delete user "${userName}"?\n\nThis action cannot be undone and will:\n• Delete all user data\n• Remove from all groups\n• Delete wishlists and items\n• Cancel subscriptions\n• Remove all roles and permissions`;

    if (!window.confirm(confirmMessage)) return;

    // Additional confirmation for safety
    const finalConfirm = window.prompt(`Type "DELETE" to confirm deletion of user "${userName}":`);
    if (finalConfirm !== 'DELETE') {
      alert('Deletion cancelled - confirmation not entered correctly.');
      return;
    }

    try {
      await axios.delete(`/admin/users/${userId}`);
      alert('User deleted successfully!');
      loadUsers(pagination.page);
    } catch (error) {
      console.error('Failed to delete user:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      alert('Failed to delete user: ' + errorMessage);
    }
  };

  // Approve/decline a pending beta signup (see backend/routes/auth.js's
  // POST /auth/beta-signup and admin.js's approve-beta/decline-beta)
  const approveBeta = async (userId) => {
    try {
      const response = await axios.post(`/admin/users/${userId}/approve-beta`);
      alert(response.data.email_sent
        ? 'Approved! Activation email sent.'
        : 'Approved, but the activation email could not be sent (email may not be configured).');
      loadUsers(pagination.page);
    } catch (error) {
      console.error('Failed to approve beta signup:', error);
      alert('Failed to approve: ' + (error.response?.data?.error || error.message));
    }
  };

  const declineBeta = async (userId) => {
    if (!window.confirm('Decline this beta access request?')) return;

    try {
      await axios.post(`/admin/users/${userId}/decline-beta`);
      alert('Declined.');
      loadUsers(pagination.page);
    } catch (error) {
      console.error('Failed to decline beta signup:', error);
      alert('Failed to decline: ' + (error.response?.data?.error || error.message));
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadUsers();
    loadAutoApproveSetting();
  }, [isAdmin]);

  const loadAutoApproveSetting = async () => {
    try {
      const response = await axios.get('/admin/settings');
      const setting = response.data.find(s => s.key === 'auto_approve_beta_signups');
      setAutoApprove(setting ? setting.value === 'true' : false);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setAutoApprove(false);
    }
  };

  const toggleAutoApprove = async () => {
    const next = !autoApprove;
    setAutoApproveSaving(true);
    try {
      await axios.put(`/admin/settings/auto_approve_beta_signups`, { value: next });
      setAutoApprove(next);
    } catch (error) {
      console.error('Failed to update setting:', error);
      alert('Failed to update setting: ' + (error.response?.data?.error || error.message));
    }
    setAutoApproveSaving(false);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers(1, searchTerm);
  };

  // Check if email exists
  const checkEmailAvailability = async (email) => {
    if (!email || !email.includes('@')) {
      setEmailCheckStatus(null);
      return;
    }

    setEmailCheckStatus('checking');
    try {
      // Check if any user has this email
      const existingUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
      
      if (existingUser) {
        setEmailCheckStatus('taken');
      } else {
        setEmailCheckStatus('available');
      }
    } catch (error) {
      setEmailCheckStatus(null);
    }
  };

  // Create user
  const createUser = async () => {
    if (emailCheckStatus === 'taken') {
      alert('This email is already registered. Please use a different email.');
      return;
    }

    try {
      const response = await axios.post('/admin/users', newUser);
      setCreateUserResult(response.data);
      alert('User created successfully!\n\n' + 
        (response.data.tempPassword ? `Temporary password: ${response.data.tempPassword}\n\nPlease save this password securely!` : ''));
      loadUsers(pagination.page);
      
      // Don't close modal immediately if there's a temp password to show
      if (!response.data.tempPassword) {
        setShowCreateUserModal(false);
        setNewUser({ email: '', name: '', password: '', planType: 'free' });
        setEmailCheckStatus(null);
      }
    } catch (error) {
      console.error('Failed to create user:', error);
      alert('Failed to create user: ' + (error.response?.data?.error || error.message));
    }
  };

  // Access denied for non-admins
  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '2rem',
      paddingTop: '5rem',
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #141517 0%, #1f2023 100%)',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2.5rem',
          background: 'linear-gradient(135deg, #c2555c 0%, #9c3f45 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '0.5rem'
        }}>
          👥 User Management
        </h1>
        <p style={{ color: '#9ca3af', fontSize: '1.1rem' }}>
          Manage users, roles, and subscriptions across the platform
        </p>
      </div>

      {/* Auto-approve toggle */}
      {autoApprove !== null && (
        <div style={{
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div>
            <div style={{ color: 'white', fontWeight: '600' }}>Auto-approve beta signups</div>
            <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
              {autoApprove
                ? 'New requests are approved and emailed an activation link immediately — no manual review.'
                : 'New requests wait in the pending queue below until approved or declined.'}
            </div>
          </div>
          <button
            onClick={toggleAutoApprove}
            disabled={autoApproveSaving}
            style={{
              padding: '0.6rem 1.25rem',
              background: autoApprove ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              border: `1px solid ${autoApprove ? 'rgba(34, 197, 94, 0.4)' : 'rgba(255, 255, 255, 0.2)'}`,
              borderRadius: '8px',
              color: autoApprove ? '#22c55e' : '#9ca3af',
              cursor: autoApproveSaving ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              minWidth: '90px'
            }}
          >
            {autoApproveSaving ? '...' : autoApprove ? 'ON' : 'OFF'}
          </button>
        </div>
      )}

      {/* Search and Stats */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'rgba(194, 85, 92, 0.1)',
          border: '1px solid rgba(194, 85, 92, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#c2555c' }}>
            {pagination.total}
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Total Users</div>
        </div>

        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#22c55e' }}>
            {users.filter(u => u.subscription_status === 'active').length}
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Active Subs</div>
        </div>

        <div style={{
          background: 'rgba(234, 179, 8, 0.1)',
          border: '1px solid rgba(234, 179, 8, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#eab308' }}>
            {users.filter(u => u.plan_type === 'pro').length}
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Pro Users</div>
        </div>

        <div style={{
          background: 'rgba(168, 85, 247, 0.1)',
          border: '1px solid rgba(168, 85, 247, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#a855f7' }}>
            {users.filter(u => u.roles && u.roles.length > 0).length}
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>With Roles</div>
        </div>
      </div>

      {/* Create User Button */}
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => setShowCreateUserModal(true)}
          style={{
            padding: '1rem 2rem',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ➕ Create New User
        </button>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: '1rem',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '1rem'
            }}
          />
          <button
            type="submit"
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #c2555c 0%, #9c3f45 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            🔍 Search
          </button>
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                loadUsers(1, '');
              }}
              style={{
                padding: '1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          )}
        </div>
      </form>

      {/* Users Table */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
            Loading users...
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
            No users found
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(194, 85, 92, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#c2555c', fontWeight: 'bold' }}>User</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#c2555c', fontWeight: 'bold' }}>Subscription</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#c2555c', fontWeight: 'bold' }}>Roles</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#c2555c', fontWeight: 'bold' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#c2555c', fontWeight: 'bold' }}>Joined</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#c2555c', fontWeight: 'bold' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr key={u.id} style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  background: idx % 2 === 0 ? 'rgba(0, 0, 0, 0.2)' : 'transparent'
                }}>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {u.profile_picture_url ? (
                        <img
                          src={u.profile_picture_url}
                          alt={u.name}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #c2555c 0%, #9c3f45 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div style={{ color: 'white', fontWeight: '500' }}>{u.name}</div>
                        <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      background: u.plan_type === 'pro'
                        ? 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)'
                        : 'rgba(156, 163, 175, 0.2)',
                      color: u.plan_type === 'pro' ? '#141517' : '#9ca3af'
                    }}>
                      {u.plan_type?.toUpperCase() || 'FREE'}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {u.roles && u.roles.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {u.roles.map((roleObj, i) => (
                          <span
                            key={i}
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              background: 'rgba(194, 85, 92, 0.2)',
                              color: '#c2555c',
                              border: '1px solid rgba(194, 85, 92, 0.3)'
                            }}
                          >
                            {roleObj.role}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>No roles</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      background: u.account_status === 'pending'
                        ? 'rgba(234, 179, 8, 0.2)'
                        : u.account_status === 'declined'
                          ? 'rgba(220, 38, 38, 0.2)'
                          : 'rgba(34, 197, 94, 0.15)',
                      color: u.account_status === 'pending'
                        ? '#eab308'
                        : u.account_status === 'declined'
                          ? '#9c3f45'
                          : '#22c55e'
                    }}>
                      {(u.account_status || 'approved').toUpperCase()}
                    </div>
                  </td>
                  <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.85rem' }}>
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                      {u.account_status === 'pending' && (
                        <>
                          <button
                            onClick={() => approveBeta(u.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(34, 197, 94, 0.2)',
                              border: '1px solid rgba(34, 197, 94, 0.3)',
                              borderRadius: '6px',
                              color: '#22c55e',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => declineBeta(u.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(220, 38, 38, 0.2)',
                              border: '1px solid rgba(220, 38, 38, 0.3)',
                              borderRadius: '6px',
                              color: '#9c3f45',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            ✕ Decline
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => {
                          setSelectedUser(u);
                          setShowRoleModal(true);
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(194, 85, 92, 0.2)',
                          border: '1px solid rgba(194, 85, 92, 0.3)',
                          borderRadius: '6px',
                          color: '#c2555c',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        🎭 Roles
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(u);
                          setShowSubscriptionModal(true);
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(234, 179, 8, 0.2)',
                          border: '1px solid rgba(234, 179, 8, 0.3)',
                          borderRadius: '6px',
                          color: '#eab308',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        💳 Plan
                      </button>
                      <button
                        onClick={() => deleteUser(u.id, u.name)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(220, 38, 38, 0.2)',
                          border: '1px solid rgba(220, 38, 38, 0.3)',
                          borderRadius: '6px',
                          color: '#9c3f45',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => loadUsers(pagination.page - 1)}
            disabled={pagination.page === 1}
            style={{
              padding: '0.5rem 1rem',
              background: pagination.page === 1 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(194, 85, 92, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              color: pagination.page === 1 ? '#6b7280' : '#c2555c',
              cursor: pagination.page === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            ← Previous
          </button>
          <span style={{ padding: '0.5rem 1rem', color: '#9ca3af' }}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => loadUsers(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            style={{
              padding: '0.5rem 1rem',
              background: pagination.page === pagination.totalPages ? 'rgba(255, 255, 255, 0.05)' : 'rgba(194, 85, 92, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              color: pagination.page === pagination.totalPages ? '#6b7280' : '#c2555c',
              cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Next →
          </button>
        </div>
      )}

      {/* Role Modal */}
      {showRoleModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1f2023 0%, #141517 100%)',
            border: '1px solid rgba(194, 85, 92, 0.3)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ color: '#c2555c', marginBottom: '1rem' }}>
              Manage Roles: {selectedUser.name}
            </h3>
            
            {/* Current Roles */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                Current Roles:
              </div>
              {selectedUser.roles && selectedUser.roles.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {selectedUser.roles.map((roleObj, i) => (
                    <div key={i} style={{
                      padding: '0.5rem 1rem',
                      background: 'rgba(194, 85, 92, 0.2)',
                      border: '1px solid rgba(194, 85, 92, 0.3)',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ color: '#c2555c', fontWeight: 'bold' }}>
                        {roleObj.role}
                      </span>
                      <button
                        onClick={() => revokeRole(selectedUser.id, roleObj.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#c2555c',
                          cursor: 'pointer',
                          padding: '0.25rem'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#6b7280' }}>No roles assigned</div>
              )}
            </div>

            {/* Grant New Role */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                Grant New Role:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {['admin', 'super_admin', 'support'].map(role => (
                  <button
                    key={role}
                    onClick={() => grantRole(selectedUser.id, role)}
                    disabled={selectedUser.roles?.some(r => r.role === role)}
                    style={{
                      padding: '0.75rem',
                      background: selectedUser.roles?.some(r => r.role === role)
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(194, 85, 92, 0.2)',
                      border: '1px solid rgba(194, 85, 92, 0.3)',
                      borderRadius: '6px',
                      color: selectedUser.roles?.some(r => r.role === role) ? '#6b7280' : '#c2555c',
                      cursor: selectedUser.roles?.some(r => r.role === role) ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}
                  >
                    {role.replace('_', ' ').toUpperCase()}
                    {selectedUser.roles?.some(r => r.role === role) && ' (Already assigned)'}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => {
                setShowRoleModal(false);
                setSelectedUser(null);
              }}
              style={{
                padding: '0.75rem',
                width: '100%',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1f2023 0%, #141517 100%)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#22c55e', marginBottom: '1.5rem' }}>
              ➕ Create New User
            </h3>

            {createUserResult ? (
              <div>
                <div style={{
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{ color: '#22c55e', fontWeight: 'bold', marginBottom: '1rem' }}>
                    ✅ User Created Successfully!
                  </p>
                  <div style={{ color: '#9ca3af', marginBottom: '0.5rem' }}>
                    <strong>Email:</strong> {createUserResult.user.email}
                  </div>
                  <div style={{ color: '#9ca3af', marginBottom: '0.5rem' }}>
                    <strong>Name:</strong> {createUserResult.user.name}
                  </div>
                  <div style={{ color: '#9ca3af', marginBottom: '0.5rem' }}>
                    <strong>Plan:</strong> {createUserResult.user.planType.toUpperCase()}
                  </div>
                  
                  {createUserResult.tempPassword && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: 'rgba(234, 179, 8, 0.1)',
                      border: '1px solid rgba(234, 179, 8, 0.3)',
                      borderRadius: '6px'
                    }}>
                      <p style={{ color: '#eab308', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        ⚠️ Temporary Password (save this!):
                      </p>
                      <p style={{ 
                        color: 'white',
                        fontFamily: 'monospace',
                        fontSize: '1.2rem',
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        userSelect: 'all',
                        wordBreak: 'break-all'
                      }}>
                        {createUserResult.tempPassword}
                      </p>
                      <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        Please share this password with the user securely. They can change it after logging in.
                      </p>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    setShowCreateUserModal(false);
                    setNewUser({ email: '', name: '', password: '', planType: 'free' });
                    setCreateUserResult(null);
                  }}
                  style={{
                    padding: '0.75rem',
                    width: '100%',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => {
                      setNewUser({ ...newUser, email: e.target.value });
                      checkEmailAvailability(e.target.value);
                    }}
                    placeholder="user@example.com"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: `1px solid ${
                        emailCheckStatus === 'taken' ? '#c2555c' :
                        emailCheckStatus === 'available' ? '#22c55e' :
                        'rgba(255, 255, 255, 0.2)'
                      }`,
                      borderRadius: '6px',
                      color: 'white'
                    }}
                  />
                  {emailCheckStatus === 'checking' && (
                    <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      ⏳ Checking availability...
                    </p>
                  )}
                  {emailCheckStatus === 'available' && (
                    <p style={{ color: '#22c55e', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      ✅ Email is available
                    </p>
                  )}
                  {emailCheckStatus === 'taken' && (
                    <p style={{ color: '#c2555c', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      ⚠️ This email is already registered
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="John Doe"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: 'white'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Password (optional - will auto-generate if empty)
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Leave empty to auto-generate"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: 'white'
                    }}
                  />
                  <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                    If empty, a temporary password will be generated and displayed
                  </p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Subscription Plan
                  </label>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      type="button"
                      onClick={() => setNewUser({ ...newUser, planType: 'free' })}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: newUser.planType === 'free'
                          ? 'rgba(156, 163, 175, 0.3)'
                          : 'rgba(156, 163, 175, 0.1)',
                        border: `2px solid ${newUser.planType === 'free' ? '#9ca3af' : 'rgba(156, 163, 175, 0.3)'}`,
                        borderRadius: '6px',
                        color: '#9ca3af',
                        cursor: 'pointer',
                        fontWeight: newUser.planType === 'free' ? 'bold' : 'normal'
                      }}
                    >
                      FREE
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewUser({ ...newUser, planType: 'pro' })}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: newUser.planType === 'pro'
                          ? 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)'
                          : 'rgba(234, 179, 8, 0.1)',
                        border: `2px solid ${newUser.planType === 'pro' ? '#eab308' : 'rgba(234, 179, 8, 0.3)'}`,
                        borderRadius: '6px',
                        color: newUser.planType === 'pro' ? '#141517' : '#eab308',
                        cursor: 'pointer',
                        fontWeight: newUser.planType === 'pro' ? 'bold' : 'normal'
                      }}
                    >
                      PRO
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={() => {
                      setShowCreateUserModal(false);
                      setNewUser({ email: '', name: '', password: '', planType: 'free' });
                      setCreateUserResult(null);
                      setEmailCheckStatus(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createUser}
                    disabled={!newUser.email || !newUser.name || emailCheckStatus === 'taken' || emailCheckStatus === 'checking'}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: (!newUser.email || !newUser.name)
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      border: 'none',
                      borderRadius: '6px',
                      color: (!newUser.email || !newUser.name || emailCheckStatus === 'taken' || emailCheckStatus === 'checking') ? '#6b7280' : 'white',
                      cursor: (!newUser.email || !newUser.name || emailCheckStatus === 'taken' || emailCheckStatus === 'checking') ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Create User
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1f2023 0%, #141517 100%)',
            border: '1px solid rgba(234, 179, 8, 0.3)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ color: '#eab308', marginBottom: '1rem' }}>
              Manage Subscription: {selectedUser.name}
            </h3>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                Current Plan:
              </div>
              <div style={{
                padding: '1rem',
                background: 'rgba(234, 179, 8, 0.1)',
                border: '1px solid rgba(234, 179, 8, 0.3)',
                borderRadius: '6px',
                color: '#eab308',
                fontWeight: 'bold',
                fontSize: '1.2rem'
              }}>
                {selectedUser.plan_type?.toUpperCase() || 'FREE'}
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                Change To:
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => updateSubscription(selectedUser.id, 'free')}
                  disabled={selectedUser.plan_type === 'free'}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: selectedUser.plan_type === 'free'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(156, 163, 175, 0.2)',
                    border: '1px solid rgba(156, 163, 175, 0.3)',
                    borderRadius: '6px',
                    color: selectedUser.plan_type === 'free' ? '#6b7280' : '#9ca3af',
                    cursor: selectedUser.plan_type === 'free' ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  FREE
                </button>
                <button
                  onClick={() => updateSubscription(selectedUser.id, 'pro')}
                  disabled={selectedUser.plan_type === 'pro'}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: selectedUser.plan_type === 'pro'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: selectedUser.plan_type === 'pro' ? '#6b7280' : '#141517',
                    cursor: selectedUser.plan_type === 'pro' ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  PRO
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                setShowSubscriptionModal(false);
                setSelectedUser(null);
              }}
              style={{
                padding: '0.75rem',
                width: '100%',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
